import 'sharer.js';

import Modal from 'CoreComponents/modal/';
import SelectInput from 'CoreComponents/selectInput';
import VideoPlayer from 'CoreComponents/videoPlayer';
import { NAVIGATION_TAB_CHANGED } from 'CoreComponents/navTab/events';

import AvailabilityTimetableForm from '../components/availabilityTimetableForm/';
import PhotographerProfileInfo from '../components/photographerProfileInfo/';
import PhotographerPackageList from '../components/photographerPackageList';

import {
  PHOTOGRAPHER_AVAILABILITIES_SELECT_CATEGORY,
  PHOTOGRAPHER_AVAILABILITIES_SELECT_PACKAGE
} from '../components/availabilityTimetableForm/events';

import {
  PHOTOGRAPHER_PACKAGE_LIST_VISIBLE_CATEGORY_PACKAGE
} from '../components/photographerPackageList/events';

const SELECTOR = {
  profileInfo: '.photographer-profile-info',
  availabilityCategoryHeaderSelect: '.form-availability-header select',
  availabilityCategorySelect: '.photographer-profile-info__booking select',
  availabilityTimetable: '.form-availability-timetable',
  categoryPackageList: '.list-package-set',
  linkToSeeReviews: '.btn--link-secondary',
  videoPlayer: '[id^=videoPlayer]',
  appHeader: '.app-header',
  // Elements with event listeners
  photographerProfileContent: '.photographer-profile-content',
  priceListButton: '.photographer-profile-info__booking .btn--link',
  seePricesButton: '.btn--see-price',
  packageListItem: '.card-package__btn-action'
};

const TABS = {
  prices: 'pricesPane',
  portfolio: 'portfolioPane'
};

const CLASS_NAMES = {
  instantBooking: 'photographers--instant-booking'
};

class PhotographerInfoScreen {
  constructor() {
    this.profileInfo = document.querySelector(SELECTOR['profileInfo']);
    this.isInstantBooking = document.body.classList.contains(CLASS_NAMES['instantBooking']);
    this.availabilityTimetable = document.querySelector(SELECTOR['availabilityTimetable']);
    this.availabilityCategoryHeaderSelect = document.querySelector(SELECTOR['availabilityCategoryHeaderSelect']);
    this.availabilityCategorySelect = document.querySelector(SELECTOR['availabilityCategorySelect']);
    this.categoryPackageList = document.querySelector(SELECTOR['categoryPackageList']);
    this.seePricesButton = document.querySelector(SELECTOR['seePricesButton']);
    this.photographerProfileContent = document.querySelector(SELECTOR['photographerProfileContent']);
    this.linkToSeeReviews = document.querySelector(SELECTOR['linkToSeeReviews']);
    this.videoPlayers = document.querySelectorAll(SELECTOR['videoPlayer']);

    // Bind functions
    this.categoryChangeHandler = this.categoryChangeHandler.bind(this);
    this.packageCategoryChangeHandler = this.packageCategoryChangeHandler.bind(this);
    this.packageClickHandler = this.packageClickHandler.bind(this);
    this.onNavigationTabChange = this.onNavigationTabChange.bind(this);
    this.scrollHandler = this.scrollHandler.bind(this);

    this._setup();
    this._addEventListeners();
  }

  // Event Handlers
  /**
   * Event handler when a package is selected
   * Dispatches a custom event to the `AvailabilityTimetableForm` and `PhotographerPackageList` components
   *
   * @param {Event} [event] - `change` event.
   * */
  packageCategoryChangeHandler(event) {
    const categoryId = event.currentTarget.value;

    this._dispatchCategorySelectEvent(categoryId);
    this._dispatchPhotographerPackageListEvent(categoryId);
  }

  /**
   * Event handler triggered when category select elements are changed.
   *
   * @param {Event} event - click event.
   * */
  categoryChangeHandler(event) {
    this.availabilityCategoryHeaderSelect.value = event.target.value;
    this.availabilityCategorySelect.value = event.target.value;
  }

  /**
   * Event handler when a package is selected in the package list
   * Dispatches a custom event to the `AvailabilityTimetableForm` component
   *
   * @param {Event} [event] - `click` event.
   * */
  packageClickHandler(event) {
    const { categoryId, packageId } = event.currentTarget.dataset;

    this._dispatchPackageSelectEvent(packageId, categoryId);
  }

  /**
   * Scroll handler of reviews link.
   *  Finds the closest link clicked.
   *    1) If the href is a hash url, enables smooth scroll.
   *    2) Else allows the default browser action.
   *
   * @param {Event} event - scroll event on reviews link.
   * */
  scrollHandler(event) {
    const closestLink = event.target.closest('a');
    const targetHref = closestLink.hash;
    const scrollToEl = document.querySelector(targetHref);

    const invalidLink = (closestLink.pathname !== window.location.pathname) || targetHref.includes('#');
    const missingScrollElement = scrollToEl === null;

    if (invalidLink || missingScrollElement) { return; }

    event.preventDefault();

    const appHeader = document.querySelector(SELECTOR['appHeader']);
    const appHeaderHeight = appHeader.getBoundingClientRect().height;

    // compensate for the header height, as its fixed at top
    const verticalScroll = scrollToEl.getBoundingClientRect().y - appHeaderHeight;

    // Enable Smooth scroll
    window.scrollBy({
      top: verticalScroll,
      left: 0,
      behavior: 'smooth'
    });
  }

  onNavigationTabChange(event) {
    if (event.detail.tab === TABS['prices']) {
      this.seePricesButton.setAttribute('hidden', true);
    } else if (event.detail.tab === TABS['portfolio']) {
      this.seePricesButton.removeAttribute('hidden');
    }
  }

  _setup() {
    if (!this.isInstantBooking) {
      this.profileInfo && new PhotographerProfileInfo(this.profileInfo);
      this.availabilityTimetable && new AvailabilityTimetableForm(this.availabilityTimetable);
      this.availabilityCategoryHeaderSelect && new SelectInput(this.availabilityCategoryHeaderSelect);
      this.availabilityCategorySelect && new SelectInput(this.availabilityCategorySelect);
      this.categoryPackageList && new PhotographerPackageList(this.categoryPackageList);

      this._dispatchPhotographerPackageListEvent(this.availabilityCategorySelect.value);
    }

    this._setupVideoPlayers();
  }

  _addEventListeners() {
    if (!this.isInstantBooking) {
      // As there are two category select elements, symlink their values
      document.querySelector(SELECTOR['availabilityCategoryHeaderSelect']).addEventListener('change', this.categoryChangeHandler);
      document.querySelector(SELECTOR['availabilityCategorySelect']).addEventListener('change', this.categoryChangeHandler);

      // Actual change category handler to update the availability check
      document.querySelector(SELECTOR['availabilityCategoryHeaderSelect']).addEventListener('change', this.packageCategoryChangeHandler);
      document.querySelector(SELECTOR['availabilityCategorySelect']).addEventListener('change', this.packageCategoryChangeHandler);

      document.querySelectorAll(SELECTOR['packageListItem']).forEach(packageListItem => {
        packageListItem.addEventListener('click', this.packageClickHandler);
      });
    }
    this.photographerProfileContent.addEventListener(NAVIGATION_TAB_CHANGED, this.onNavigationTabChange);

    this.linkToSeeReviews && this.linkToSeeReviews.addEventListener('click', this.scrollHandler);
  }

  _dispatchCategorySelectEvent(categoryId) {
    const categorySelectEvent = new CustomEvent(PHOTOGRAPHER_AVAILABILITIES_SELECT_CATEGORY, {
      detail: {
        categoryId: categoryId
      }
    });

    this.availabilityTimetable.dispatchEvent(categorySelectEvent);
  }

  _dispatchPhotographerPackageListEvent(categoryId) {
    const visibleCategoryPackageEvent = new CustomEvent(PHOTOGRAPHER_PACKAGE_LIST_VISIBLE_CATEGORY_PACKAGE, {
      detail: {
        categoryId: categoryId
      }
    });

    this.categoryPackageList.dispatchEvent(visibleCategoryPackageEvent);
  }

  _dispatchPackageSelectEvent(packageId, categoryId) {
    const packageSelectEvent = new CustomEvent(PHOTOGRAPHER_AVAILABILITIES_SELECT_PACKAGE, {
      detail: {
        categoryId: categoryId,
        packageId: packageId
      }
    });

    this.availabilityTimetable.dispatchEvent(packageSelectEvent);
  }

  _setupVideoPlayers() {
    if (!(this.videoPlayers && this.videoPlayers.length)) {
      return;
    }

    this.videoPlayers.forEach(videoPlayerElement => {
      const modalId = `#${videoPlayerElement.id.replace(/Player/, 'Modal')}`;
      const videoPlayerOptions = {
        file: videoPlayerElement.dataset.videoFile,
        title: videoPlayerElement.dataset.videoTitle,
        mediaid: videoPlayerElement.dataset.videoUuid,
        type: videoPlayerElement.dataset.videoType,
        displaytitle: true
      };

      if (videoPlayerElement.dataset.videoPoster) {
        Object.assign(videoPlayerOptions, {
          image: videoPlayerElement.dataset.videoPoster
        });
      }

      const modal = new Modal(document.querySelector(modalId));
      const player = new VideoPlayer(videoPlayerElement, videoPlayerOptions);

      modal.onShowed(() => {
        player.play();
      });

      modal.onHidden(() => {
        player.pause();
      });

      player.onFullScreen((event) => {
        const { fullscreen } = event;

        if (fullscreen && document.body.classList.contains('modal__open')) {
          document.querySelector('.modal__overlay').style.display = 'none';
        } else {
          document.querySelector('.modal__overlay').style.display = 'block';
        }
      });
    });
  }
}

// Setup the AdvanceBookingPhotographerScreen class, only on the photographer profile page.
let isPhotographerInfoPage = document.body.classList.contains('photographers') && document.body.classList.contains('show');

if (isPhotographerInfoPage) {
  new PhotographerInfoScreen();
}



// WEBPACK FOOTER //
// ./engines/vcliq_booking/app/assets/javascripts/vcliq_booking/screens/PhotographerProfile.js